export interface MinifyResult {
  ok: boolean;
  output: string;
  error?: { message: string; line?: number; column?: number };
}

export async function minifyJs(code: string): Promise<MinifyResult> {
  if (!code.trim()) return { ok: true, output: "" };
  try {
    const { minify } = await import("terser");
    const r = await minify(code, { compress: true, mangle: true, format: { comments: false } });
    return { ok: true, output: r.code ?? "" };
  } catch (e) {
    const err = e as { message?: string; line?: number; col?: number };
    return { ok: false, output: "", error: { message: err.message ?? "Could not parse JavaScript", line: err.line, column: err.col } };
  }
}

export async function minifyCss(code: string): Promise<MinifyResult> {
  if (!code.trim()) return { ok: true, output: "" };
  try {
    const csso = await import("csso");
    const errors: { message: string; line?: number; column?: number }[] = [];
    // Parse first so a bad rule is reported with its position instead of being dropped silently.
    csso.syntax.parse(code, {
      positions: true,
      onParseError: (e: { message: string; line?: number; column?: number }) => errors.push(e),
    });
    if (errors.length > 0) {
      const first = errors[0];
      return { ok: false, output: "", error: { message: first.message, line: first.line, column: first.column } };
    }
    const r = csso.minify(code, { restructure: true });
    return { ok: true, output: r.css };
  } catch (e) {
    const err = e as { message?: string; line?: number; column?: number };
    return { ok: false, output: "", error: { message: err.message ?? "Could not parse CSS", line: err.line, column: err.column } };
  }
}

export function byteLength(s: string): number {
  return new TextEncoder().encode(s).length;
}

export async function gzipSize(s: string): Promise<number> {
  if (typeof CompressionStream === "undefined") return 0;
  const stream = new Blob([s]).stream().pipeThrough(new CompressionStream("gzip"));
  return (await new Response(stream).arrayBuffer()).byteLength;
}

export async function minifyHtml(code: string): Promise<MinifyResult> {
  if (!code.trim()) return { ok: true, output: "" };
  try {
    // The browser bundle (parser + CSS and JS minifiers); loaded only when someone minifies HTML.
    const mod = (await import("html-minifier-terser/dist/htmlminifier.esm.bundle")) as unknown as {
      minify: (html: string, o: Record<string, unknown>) => Promise<string>;
    };
    const out = await mod.minify(code, {
      collapseWhitespace: true,
      conservativeCollapse: false,
      removeComments: true,
      removeRedundantAttributes: true,
      removeScriptTypeAttributes: true,
      removeStyleLinkTypeAttributes: true,
      useShortDoctype: true,
      minifyCSS: true,
      minifyJS: true,
      // Keep anything that changes meaning: whitespace in <pre>/<textarea> is preserved by the library itself,
      // attribute quotes and optional tags are left alone, and conditional comments stay.
      ignoreCustomComments: [/^!/, /^\[/],
    });
    return { ok: true, output: out };
  } catch (e) {
    const err = e as { message?: string };
    const msg = err.message ?? "Could not parse HTML";
    const m = /line (\d+)/i.exec(msg);
    return { ok: false, output: "", error: { message: msg.split("\n")[0], line: m ? Number(m[1]) : undefined } };
  }
}
